import { isArray, isPlainObject } from '@art-suite/art-core-ts';
import { v4 as uuidv4 } from 'uuid';
import { pureFunctionalMap, pureFunctionalRecordMap } from './pureFunctionalLib';

/**
 * Generates a unique ID string (uuidV4)
 * @returns string
 */
export const getUniqueId = () => uuidv4();

/**
 * Estimates the size of a customer metadata object in PostgreSQL JSONB format
 *
 * Note: I did performance tests comparing this with just doing JSON.stringify().length; this was over 100x faster with node v22.17.0 on a MacBook Pro M4.
 * @param metadata - The customer metadata object to estimate the size of
 * @returns The estimated size of the customer metadata object in PostgreSQL JSONB format
 */
export const estimatePostgresJsonbSize = (metadata: Record<string, string | number | boolean>) => {
  let totalSize = 0;

  for (const key in metadata) {
    // Key overhead (PostgreSQL stores keys efficiently but with some overhead)
    totalSize += key.length + 2; // +2 for key overhead

    const value = metadata[key];
    if (typeof value === 'string') {
      totalSize += value.length + 4; // +4 for string overhead
    } else if (typeof value === 'number') {
      totalSize += 8; // Most numbers fit in 8 bytes
    } else if (typeof value === 'boolean') {
      totalSize += 1; // Booleans are very efficient in JSONB
    } else {
      throw new Error(`Unsupported value type: ${typeof value}`);
    }
  }

  // Add some document-level overhead
  totalSize += 20;

  return totalSize;
};

export const normalizeScanOrScanTemplatePartShallow = <T>(obj: T): T => {
  let newObj: T | undefined = undefined;
  for (const key in obj) {
    const value = obj[key];
    const normalizedValue = value === '' || value === null ? undefined : value;
    if (normalizedValue !== value) {
      if (!newObj) newObj = { ...obj };
      (newObj as any)[key as any] = normalizedValue;
    }
  }
  return newObj ?? obj;
};

export const normalizeScanOrScanTemplatePartPropValues = <T>(obj: T, key?: string): T | undefined | null => {
  if (isPlainObject(obj)) {
    return pureFunctionalRecordMap(obj as Record<string, any>, (value, childKey) => normalizeScanOrScanTemplatePartPropValues(value, childKey as string)) as T;
  }
  if (isArray(obj)) {
    return pureFunctionalMap(obj as any[], value => normalizeScanOrScanTemplatePartPropValues(value)) as T;
  }
  // name is a required field on every ScanTemplatePart (and Scan part); never normalize it away to undefined
  if (key === 'name') return obj;
  if (obj === '' || obj === null) return undefined;
  return obj;
};
